import { ApiResponse } from "@/utils/ApiResponse";
import type { QueryKey } from "@tanstack/vue-query";
import { useQueryClient, useMutation } from "@tanstack/vue-query";
import { useToast } from "vue-toastification";
import { MarcaVehiculoInterface } from "../../interfaces/marcaVehiculosInterfaces/MarcaVehiculo.interface";
import { ToggleVisibilityMarcaVehiculoAction } from "../../actions/marcaVehiculosActions";

const toast = useToast();

type ToggleCtx = {
  previousEntries: [QueryKey, unknown][];
};

export function useMarcaVehiculoToggleVisib() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (marcaId: string) =>
      ToggleVisibilityMarcaVehiculoAction(marcaId),

    onMutate: async (marcaId: string): Promise<ToggleCtx> => {
      await queryClient.cancelQueries({ queryKey: ["MarcaVehiculosList"] });
      const previousEntries = queryClient.getQueriesData({
        queryKey: ["MarcaVehiculosList"],
      });

      queryClient.setQueriesData<ApiResponse<MarcaVehiculoInterface>>(
        { queryKey: ["MarcaVehiculosList"] },
        (old) => {
          if (!old?.data?.data?.marcasVehiculos) return old;
          return {
            ...old,
            data: {
              ...old.data,
              data: {
                ...old.data.data,
                marcasVehiculos: old.data.data.marcasVehiculos.map((marca) =>
                  marca.id === marcaId
                    ? { ...marca, isVisible: marca.isVisible === 1 ? 0 : 1 }
                    : marca
                ),
              },
            },
          };
        }
      );

      return { previousEntries };
    },

    onError: (_error, _marcaId, context) => {
      context?.previousEntries?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      toast.error("No se pudo actualizar la visibilidad de la marca de vehículo.");
    },

    onSuccess: () => {
      toast.success("Visibilidad de marca de vehículo actualizada.");
    },

    onSettled: async () => {
      await queryClient.refetchQueries({
        queryKey: ["MarcaVehiculosList"],
        exact: false,
      });
      await queryClient.refetchQueries({
        queryKey: ["MarcaModeloList"],
        exact: true,
      });
    },
  });

  return {
    ...mutation,
  };
}
